import { animate, style, transition, trigger } from '@angular/animations';
import { isPlatformBrowser, NgFor, NgIf } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { TvSlide } from '../../models/tvSlide';
import { TsHttpService } from '../../services/ts-http.service';
import { TvSlideService } from '../../services/tv-slide.service';
import { PictureComponent } from '../../shared/picture/picture.component';

@Component({
    selector: 'ts-infosystem-gallery',
    templateUrl: './infosystem-gallery.component.html',
    styleUrls: ['./infosystem-gallery.component.css'],
    animations: [
        trigger("crossFade", [
            transition("* => *", [
                style({ opacity: 0 }),
                animate("1s ease-in-out", style({ opacity: 1 })),
            ]),
        ]),
    ],
    standalone: true,
    imports: [
        NgFor,
        NgIf,
        PictureComponent,
    ],
})
export class InfosystemGalleryComponent implements OnInit, OnDestroy {

  @Input() schoolId: number;
  slides: TvSlide[] = [];
  count = 0;
  currentIndex = -1;
  animationState = 0; // For triggering cross-fade animations
  private updateInterval: ReturnType<typeof setInterval>;
  private slideInterval: ReturnType<typeof setInterval>;

  constructor(
    private tvSlideSrv: TvSlideService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }  updateSlides() {
    return new Promise<void>((resolve) => {
      this.tvSlideSrv.findBySchool(this.schoolId).subscribe((slides) => {
        const oldCount = this.count;
        const oldSlides = [...this.slides]; // Make a copy to compare

        this.slides = slides;
        this.count = slides.length;

        // Only restart slideshow if slides actually changed significantly
        const slidesActuallyChanged = oldCount !== this.count ||
          JSON.stringify(oldSlides.map(s => s.id)) !== JSON.stringify(slides.map(s => s.id));

        // If this is the first time loading slides, start the slideshow
        if (oldCount === 0 && this.count > 0) {
          this.startSlideshow();
        }
        // Only restart if slides actually changed (not just refreshed)
        else if (slidesActuallyChanged) {
          console.log('Slides actually changed, restarting slideshow');
          // Adjust currentIndex if needed
          if (this.currentIndex >= this.count) {
            this.currentIndex = this.count > 0 ? 0 : -1;
          }
          // Only restart if count changed significantly
          if (this.count > 0) {
            this.startSlideshow();
          }
        } else {
          console.log('Slides refreshed but no changes detected, keeping current position');
        }

        resolve();
      });
    });
  }

  getVideoPath(filename: string) {
    return `${TsHttpService.assetsRoot}${filename}`;
  }

  incIndex() {
    if (this.count > 0) {
      this.currentIndex = this.currentIndex < this.count - 1 ? this.currentIndex + 1 : 0;
      this.animationState++; // Trigger animation
    }
  }

  startSlideshow() {
    // Clear any existing slideshow
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }

    // Start new slideshow if we have slides
    if (this.count > 0) {
      this.currentIndex = 0; // Start with first slide
      this.slideInterval = setInterval(() => {
        this.incIndex();
      }, TvSlideService.slideDuration);
    }
  }

  async ngOnInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      await this.updateSlides();

      // Update slides every hour
      this.updateInterval = setInterval(async () => {
        await this.updateSlides();
      }, 3600000);
    }
  }

  ngOnDestroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
  }

}
